import React, { useEffect, useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { useAuth } from "@/lib/auth-context";
import { getAdminStats, lookupUser, grantPlan } from "@/lib/api";
import { usePageMeta } from "@/lib/use-page-meta";
import { Loader2, Users, UserCheck, FileText, Share2, TrendingUp, Mail, KeyRound, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import BacklinkTracker from "@/components/BacklinkTracker";
import SubscribersTracker from "@/components/SubscribersTracker";

const OWNER_EMAILS = ["limitlesslegacyconsultingllc@gmail.com", "office@myplainly.com"];

function StatCard({ label, primary, sub, accent, icon: Icon, testId }) {
  return (
    <div
      className="rounded-2xl bg-white border border-black/5 bento-shadow p-5 flex gap-4 items-start"
      data-testid={testId}
    >
      <div
        className="w-10 h-10 rounded-xl grid place-items-center text-[#F7F5F0] shrink-0"
        style={{ backgroundColor: accent || "#1E392A" }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="tag-label text-[#1E392A]/55">{label}</div>
        <div className="font-display text-3xl mt-1 leading-none">{primary}</div>
        {sub && <div className="text-xs text-[#1E392A]/55 mt-2">{sub}</div>}
      </div>
    </div>
  );
}

function planBadge(plan) {
  const map = {
    agency: { c: "bg-[#1E392A] text-[#85CDCB]", l: "Agency" },
    monthly: { c: "bg-[#85CDCB]/30 text-[#1E392A]", l: "Business" },
    onetime: { c: "bg-[#E27D60]/15 text-[#a8492a]", l: "One-time" },
    free: { c: "bg-[#EAE7DD] text-[#1E392A]/65", l: "Free" },
  };
  const s = map[plan] || map.free;
  return <span className={`text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full ${s.c}`}>{s.l}</span>;
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);

  usePageMeta({ title: "Admin", description: "Owner-only Plainly stats.", path: "/admin" });

  const isOwner = !!user && OWNER_EMAILS.includes((user.email || "").toLowerCase());

  useEffect(() => {
    if (!isOwner) return;
    let alive = true;
    (async () => {
      try {
        const data = await getAdminStats();
        if (alive) setStats(data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Couldn't load stats");
      }
    })();
    return () => { alive = false; };
  }, [isOwner]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#1E392A]" />
      </div>
    );
  }

  if (!user || !isOwner) {
    return (
      <div className="min-h-screen bg-[#F7F5F0]">
        <SiteHeader />
        <div className="max-w-2xl mx-auto px-6 md:px-8 py-20 text-center" data-testid="admin-not-authorized">
          <div className="tag-label text-[#E27D60]">Restricted</div>
          <h1 className="font-display text-4xl mt-3">Owner-only area</h1>
          <p className="text-[#1E392A]/70 mt-3">This dashboard is reserved for Plainly owners. Sign in with an allowlisted email to continue.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <SiteHeader />
      <main className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="tag-label text-[#E27D60]">Owner dashboard</div>
            <h1 className="font-display text-4xl mt-2">How Plainly is going.</h1>
          </div>
          {stats && (
            <div className="text-xs text-[#1E392A]/55" data-testid="admin-as-of">
              Updated {new Date(stats.as_of).toLocaleString()}
            </div>
          )}
        </div>

        {err && (
          <div className="rounded-2xl bg-[#FFE9DF] border border-[#E27D60]/30 text-[#a8492a] p-4 mb-6">
            {err}
          </div>
        )}

        {!stats && !err && (
          <div className="rounded-2xl bg-white border border-black/5 p-10 flex items-center justify-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-[#1E392A]" />
            <span className="text-sm text-[#1E392A]/70">Loading stats…</span>
          </div>
        )}

        {stats && (
          <>
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stat-cards">
              <StatCard
                testId="admin-stat-users"
                icon={Users}
                accent="#1E392A"
                label="Total users"
                primary={stats.users.total.toLocaleString()}
                sub={`+${stats.users.signups_7d} in last 7 days · +${stats.users.signups_24h} in last 24h`}
              />
              <StatCard
                testId="admin-stat-active"
                icon={UserCheck}
                accent="#85CDCB"
                label="Active users (ran an analysis)"
                primary={stats.users.active_7d.toLocaleString()}
                sub={`Last 7d · ${stats.users.active_30d.toLocaleString()} in last 30d`}
              />
              <StatCard
                testId="admin-stat-paid"
                icon={TrendingUp}
                accent="#E27D60"
                label="Paid users"
                primary={stats.paid.total.toLocaleString()}
                sub={`${stats.paid.by_plan.monthly} Business · ${stats.paid.by_plan.agency} Agency · ${stats.paid.by_plan.onetime} One-time`}
              />
              <StatCard
                testId="admin-stat-analyses"
                icon={FileText}
                accent="#1E392A"
                label="Analyses (last 7d)"
                primary={stats.analyses.last_7d.toLocaleString()}
                sub={`${stats.analyses.last_7d_manual} manual · ${stats.analyses.last_7d_auto_weekly} auto-weekly`}
              />
            </section>

            <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <StatCard
                testId="admin-stat-shares"
                icon={Share2}
                accent="#85CDCB"
                label="Shares created"
                primary={stats.shares.total.toLocaleString()}
                sub={`${stats.shares.total_views.toLocaleString()} total views`}
              />
              <StatCard
                testId="admin-stat-share-clicks"
                icon={TrendingUp}
                accent="#E27D60"
                label="Referral clicks"
                primary={stats.shares.total_referral_clicks.toLocaleString()}
                sub="From shared report pages"
              />
              <StatCard
                testId="admin-stat-emails"
                icon={Mail}
                accent="#1E392A"
                label="Auto-weekly emails"
                primary={stats.analyses.last_7d_auto_weekly.toLocaleString()}
                sub="Sent in last 7 days"
              />
            </section>

            <section className="rounded-2xl bg-white border border-black/5 bento-shadow p-6 mb-8" data-testid="admin-signups-chart">
              <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                <div>
                  <div className="tag-label text-[#1E392A]/55">Signups per day</div>
                  <h2 className="font-display text-2xl mt-1">Last 30 days</h2>
                </div>
                <div className="text-xs text-[#1E392A]/55">
                  Total: <span className="font-medium text-[#1E392A]">{stats.users.signups_30d}</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={stats.signups_per_day}>
                  <defs>
                    <linearGradient id="signupsFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#E27D60" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#E27D60" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAE7DD" />
                  <XAxis
                    dataKey="date"
                    stroke="#8E8D8A"
                    fontSize={11}
                    tickFormatter={(d) => {
                      const dt = new Date(d);
                      return `${dt.getMonth() + 1}/${dt.getDate()}`;
                    }}
                  />
                  <YAxis stroke="#8E8D8A" fontSize={11} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid #EAE7DD" }}
                    labelFormatter={(d) => new Date(d).toLocaleDateString()}
                  />
                  <Area
                    type="monotone"
                    dataKey="signups"
                    stroke="#E27D60"
                    strokeWidth={2.5}
                    fill="url(#signupsFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </section>

            <BacklinkTracker />

            <SubscribersTracker />

            <section className="rounded-2xl bg-white border border-black/5 bento-shadow p-6" data-testid="admin-recent-signups">
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <div className="tag-label text-[#1E392A]/55">Recent signups</div>
                  <h2 className="font-display text-2xl mt-1">Last 20 users</h2>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-[#1E392A]/55">
                      <th className="pb-3 font-semibold">Email</th>
                      <th className="pb-3 font-semibold">Name</th>
                      <th className="pb-3 font-semibold">Plan</th>
                      <th className="pb-3 font-semibold">GA</th>
                      <th className="pb-3 font-semibold text-right">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_signups.length === 0 && (
                      <tr><td colSpan={5} className="py-6 text-center text-[#1E392A]/55">No signups yet.</td></tr>
                    )}
                    {stats.recent_signups.map((u, i) => (
                      <tr key={i} className="border-t border-black/5" data-testid={`admin-signup-${i}`}>
                        <td className="py-3 pr-3 text-[#1E392A]">{u.email}</td>
                        <td className="py-3 pr-3 text-[#1E392A]/75">{u.name || "—"}</td>
                        <td className="py-3 pr-3">{planBadge(u.plan)}</td>
                        <td className="py-3 pr-3">
                          {u.ga_connected ? (
                            <span className="text-xs text-[#1E392A]">Connected</span>
                          ) : (
                            <span className="text-xs text-[#1E392A]/40">—</span>
                          )}
                        </td>
                        <td className="py-3 text-right text-[#1E392A]/70 whitespace-nowrap">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
